import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { NAV } from "@/lib/config";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Anchor, Search, Plus, Bell, LogOut, ChevronDown, ChevronRight, Circle, Mail, Clock } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ComposeEmail from "@/components/ComposeEmail";
import { HelpButton } from "@/components/HelpAndReport";

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef();
  const routeFor = { parties: "/clients", policies: "/policies", claims: "/claims", service_cases: "/service-cases",
    certificates: "/certificates", opportunities: "/opportunities", leads: "/leads", carriers: "/carriers",
    documents: "/documents", assets: "/clients" };

  useEffect(() => {
    if (q.length < 2) { setGroups([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setGroups(data.groups); setOpen(true); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (resource, item) => {
    setOpen(false); setQ("");
    if (resource === "parties") nav(`/clients/${item.id}`);
    else if (item.party_id) nav(`/clients/${item.party_id}`);
    else nav(routeFor[resource] || "/");
  };

  return (
    <div className="relative w-full max-w-xl" ref={ref}>
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-mutedsea" />
      <Input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => q.length >= 2 && setOpen(true)}
        placeholder="Search clients, policies, claims, certificates…" className="pl-9 h-9 text-sm bg-white border-border" />
      {open && groups.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-border rounded-sm shadow-md max-h-96 overflow-y-auto end-scroll" data-testid="search-results">
          {groups.map((g) => (
            <div key={g.resource}>
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide font-semibold text-mutedsea bg-cloud sticky top-0">{g.label}</div>
              {g.items.map((it) => (
                <button key={it.id} onClick={() => go(g.resource, it)} data-testid={`search-item-${it.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-ice border-b border-border last:border-0">
                  <div className="text-sm text-slate font-medium">{it.label}</div>
                  {it.sublabel && <div className="text-xs text-mutedsea">{it.sublabel}</div>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {open && q.length >= 2 && groups.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-border rounded-sm shadow-md p-3 text-sm text-mutedsea">No matches.</div>
      )}
    </div>
  );
}

function QuickCreate() {
  const nav = useNavigate();
  const items = [
    { label: "Client", to: "/clients?new=person" },
    { label: "Prospect", to: "/leads?new=1" },
    { label: "Business Relationship", to: "/relationships?new=1" },
    { label: "Contact", to: "/contacts" },
    { label: "Opportunity", to: "/opportunities?new=1" },
    { label: "Policy", to: "/policies?new=1" },
    { label: "Claim", to: "/claims?new=1" },
    { label: "Certificate", to: "/certificates?new=1" },
    { label: "Activity", to: "/activities?new=1" },
    { label: "Task", to: "/tasks?new=1" },
    { label: "Appointment", to: "/activities?new=1" },
    { label: "Note", to: "/activities?new=1" },
    { label: "Document", to: "/documents?new=1" },
    { label: "Follow-Up", to: "/tasks?new=1" },
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="quick-create" className="flex items-center gap-1.5 bg-navy hover:bg-atlantic text-white px-3 h-9 rounded-sm text-sm font-medium nav-link">
          <Plus className="h-4 w-4" /> Quick Create <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-white w-56 max-h-[70vh] overflow-y-auto end-scroll">
        <DropdownMenuLabel className="text-xs text-mutedsea">Create New</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((i) => (
          <DropdownMenuItem key={i.label} data-testid={`quick-${i.label.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => nav(i.to)} className="text-sm cursor-pointer">{i.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const RECENT_ROUTE = {
  parties: (i) => `/clients/${i.record_id}`,
  policies: (i) => `/policies/${i.record_id}`,
  business_relationships: (i) => `/relationships/${i.record_id}`,
  opportunities: () => `/opportunities`,
  claims: () => `/claims`,
  contacts: (i) => (i.party_id ? `/clients/${i.party_id}` : `/contacts`),
};

function RecentlyAccessed() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/agency/recent").then(({ data }) => setItems(data.items || [])).catch(() => {});
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="recently-accessed" className="p-2 rounded-sm hover:bg-ice nav-link" title="Recently accessed">
          <Clock className="h-5 w-5 text-slate" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-white w-72">
        <DropdownMenuLabel className="font-serif text-navy">Recently Accessed</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? <div className="px-3 py-3 text-sm text-mutedsea">Nothing yet — open a record to see it here.</div> :
          items.map((i, idx) => (
            <DropdownMenuItem key={idx} data-testid={`recent-item-${idx}`} onClick={() => nav((RECENT_ROUTE[i.resource] || (() => "/"))(i))} className="cursor-pointer">
              <div className="min-w-0">
                <div className="text-sm text-slate truncate">{i.label || i.record_id}</div>
                <div className="text-[10px] uppercase text-mutedsea">{i.resource.replace("_", " ")}</div>
              </div>
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Notifications() {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/notifications"); setNotes(data.items); } catch {} })();
  }, []);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications" className="relative p-2 rounded-sm hover:bg-ice nav-link">
          <Bell className="h-5 w-5 text-slate" />
          {notes.length > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-seared" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-white w-80">
        <DropdownMenuLabel className="font-serif text-navy">Open Work</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notes.length === 0 ? <div className="px-3 py-3 text-sm text-mutedsea">Nothing needs attention.</div> :
          notes.map((n, i) => (
            <div key={i} className="px-3 py-2 flex items-start gap-2 border-b border-border last:border-0">
              <Circle className={`h-2 w-2 mt-1.5 ${n.level === "critical" ? "text-seared fill-seared" : "text-seaamber fill-seaamber"}`} />
              <span className="text-sm text-slate">{n.text}</span>
            </div>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout, level } = useAuth();
  const nav = useNavigate();
  const canWrite = level >= 40;
  const [compose, setCompose] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const toggle = (name) => setCollapsed((c) => ({ ...c, [name]: !c[name] }));

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left nav */}
      <aside className="w-64 bg-deepnavy text-white flex flex-col shrink-0">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <Anchor className="h-6 w-6 text-sky" />
          <div className="leading-tight">
            <div className="font-serif text-xl tracking-wide">ENDURANCE</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-sky/70">Casualty Insurance</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto end-scroll py-3">
          {NAV.map((grp, gi) => {
            const visibleItems = grp.items.filter((it) => !it.minLevel || level >= it.minLevel);
            if (visibleItems.length === 0) return null;
            const isOpen = grp.collapsible ? !collapsed[grp.section] : true;
            return (
              <div key={gi} className="mb-1">
                {grp.section && (grp.collapsible ? (
                  <button data-testid={`nav-group-${grp.section.toLowerCase().replace(/\s/g, "-")}`} onClick={() => toggle(grp.section)}
                    className="w-full flex items-center justify-between px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] text-sky/50 hover:text-sky/80 nav-link">
                    <span>{grp.section}</span>
                    {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>
                ) : <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] text-sky/50">{grp.section}</div>)}
                {isOpen && visibleItems.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid}
                    className={({ isActive }) =>
                      `nav-link flex items-center gap-3 px-5 py-2 text-sm ${isActive ? "bg-atlantic text-white border-l-2 border-sky" : "text-sky/80 hover:bg-white/5 hover:text-white border-l-2 border-transparent"}`}>
                    <it.icon className="h-4 w-4 shrink-0" /> {it.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-full bg-atlantic flex items-center justify-center text-xs font-semibold shrink-0">
              {(user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div className="min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[10px] text-sky/60 truncate">{user?.role_label}</div>
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] text-sky/50">
            <span className="flex items-center gap-1"><Circle className="h-2 w-2 fill-seagreen text-seagreen" /> System operational</span>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="flex items-center gap-1 hover:text-white nav-link">
              <LogOut className="h-3 w-3" /> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-border flex items-center gap-4 px-6 shrink-0">
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            {canWrite && <QuickCreate />}
            {canWrite && (
              <button data-testid="compose-email-btn" onClick={() => setCompose(true)}
                className="flex items-center gap-1.5 border border-atlantic text-atlantic hover:bg-ice px-3 h-9 rounded-sm text-sm font-medium nav-link">
                <Mail className="h-4 w-4" /> Compose
              </button>
            )}
            <HelpButton />
            <RecentlyAccessed />
            <Notifications />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 px-2 h-9 rounded-sm hover:bg-ice nav-link">
                  <div className="h-7 w-7 rounded-full bg-navy text-white flex items-center justify-center text-xs font-semibold">
                    {(user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white w-56">
                <DropdownMenuLabel>
                  <div className="text-sm text-slate">{user?.name}</div>
                  <div className="text-xs text-mutedsea">{user?.email}</div>
                  <Badge variant="outline" className="mt-1 bg-ice text-atlantic border-sky/50 font-normal">{user?.role_label}</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} className="cursor-pointer text-sm" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto end-scroll bg-cloud">
          <Outlet />
        </main>
      </div>
      <ComposeEmail open={compose} onOpenChange={setCompose} />
    </div>
  );
}
